import { useState, useEffect } from 'react'
import style from './index.module.scss'
import type { CustomizationData } from '@/services/common/types'
import type { UserInfo } from '@/services/login/types'
import HeaderAction from '../headeraction'
import UpdatePsd from '../updatePsd'
import user from '@/utils/user'
import { passwordSecurityLevel, MFA_VERIFY_TYPE, PwdValidStatus } from '@/config/default'
import intl from 'react-intl-universal'
import { Form, Input, Checkbox, Button } from 'antd'
import SvgIcon from '@/components/svgIcon'
import initFingerprint from '@/utils/fingerprint'
import { getTokenApi } from '@/services/login'
import { Encrypt } from '@/utils/aes'
import cache from '@/utils/cache'
import { loginSuccess } from '@/utils/utils'
import loginBgImage from '@/assets/images/login-back.png'
import { useRequest } from '@/hooks/useRequest'

export default function CustomLogin({
  customizationData,
  handleMfaLogin
}: {
  customizationData?: CustomizationData,
  handleMfaLogin: (params: any) => void
}) {
  const [form] = Form.useForm()
  const [loginInfo, setLoginInfo] = useState<UserInfo>()
  const [updatePsdVisible, setUpdatePsdVisible] = useState(false) // 强制更新密码弹窗是否显示
  const { runAsync: getToken, loading } = useRequest(getTokenApi, { manual: true })

  const $pleaseInput = intl.get('login.loginPleaseEnter')

  const submitLoginForm = async () => {
    try {
      const formData = await form.validateFields()
      const deviceFingerprint = await initFingerprint({ abbrName: formData.loginEnterpriseCode, account: formData.loginAccount })
      const validDaysMap = JSON.parse(localStorage.getItem('react_mfa_valid_days'))
      const key = `${formData.loginEnterpriseCode}_${formData.loginAccount}`
      const validRandomNum = validDaysMap ? validDaysMap[key] || '' : ''
      const res = await getToken({
        enterpriseCode: formData.loginEnterpriseCode,
        account: formData.loginAccount,
        password: Encrypt(formData.loginPassword),
        deviceFingerprint: Encrypt(deviceFingerprint + validRandomNum)
      })
      user.stagingUserInfo(res) // 暂存user信息
      setLoginInfo(res)
      const { pwdValidStatus } = res
      if (pwdValidStatus && pwdValidStatus !== PwdValidStatus.NORMAL) {
        setUpdatePsdVisible(true)
      } else {
        cache.localS('loginCode', formData.loginEnterpriseCode)
        if (res.mfaVerifyStatus === MFA_VERIFY_TYPE.NEED || res.mfaVerifyStatus === MFA_VERIFY_TYPE.NO_BOUND) {
          handleMfaLogin(res)
        } else {
          loginSuccess(res)
        }
      }
    } catch (error) {
      console.log(error)
    }
  }
  // 回车提交
  const enterKeyUpHandler = async (event) => {
    const nativeEvent = event.nativeEvent
    if (nativeEvent.key === 'Enter') {
      try {
        submitLoginForm()
      } catch (error) {
        console.log(error)
      }
    }
  }
  return (
    <div className={style.customLogin} style={{ backgroundImage: `url(${customizationData?.loginPageBackground || loginBgImage})` }}>
      <div className={style.header}>
        <img className={style.logo} src={customizationData?.loginPageLogo || ''} alt='' />
        <HeaderAction customizationEnable={true} />
      </div>
      <div className={style.loginForm}>
        {
          updatePsdVisible
            ? <UpdatePsd
              passwordLevel={user.isSuper() ? passwordSecurityLevel.HIGH : loginInfo?.enterprise?.securityLevel}
              passwordLengthMin={loginInfo?.enterprise?.passwordLengthMin}
              passwordLengthMax={loginInfo?.enterprise?.passwordLengthMax}
              pwdValidStatus={loginInfo?.pwdValidStatus}
              onCancel={() => setUpdatePsdVisible(false)}
            />
            : <div className={style.customForm}>
              <div className={style.title}>{intl.get('login.Welcome')}</div>
              <Form
                form={form}
                className={style.form}
              >
                <Form.Item
                  name='loginEnterpriseCode'
                  rules={[{ required: true, message: $pleaseInput + intl.get('login.enterpriseNum') }]}
                >
                  <Input
                    className={style.input}
                    prefix={<SvgIcon name='logoEnterprise' />}
                    placeholder={intl.get('login.enterpriseCode')}
                    onKeyUp={enterKeyUpHandler}
                  />
                </Form.Item>
                <Form.Item
                  name='loginAccount'
                  rules={[{ required: true, message: $pleaseInput + intl.get('login.loginNum') }]}
                >
                  <Input
                    className={style.input}
                    prefix={<SvgIcon name='loginAccount' />}
                    placeholder={intl.get('login.loginNum')}
                    onKeyUp={enterKeyUpHandler}
                  />
                </Form.Item>
                <Form.Item
                  name='loginPassword'
                  rules={[{ required: true, message: $pleaseInput + intl.get('login.password') }]}
                >
                  <Input.Password
                    className={style.input}
                    prefix={<SvgIcon name='loginPassword' />}
                    placeholder={intl.get('login.password')}
                    onKeyUp={enterKeyUpHandler}
                  />
                </Form.Item>
                <Form.Item
                  name='autoLogin'
                  className={style.checkbox}
                >
                  <Checkbox >
                    {intl.get('login.nextAutoLogin')}
                  </Checkbox>
                </Form.Item>
              </Form>
              <Button loading={loading} className={style.submitBtn} type='primary' block onClick={submitLoginForm}>{intl.get('login.loginImmediately')}</Button>
            </div>
        }
      </div>
    </div>
  )
}
